// Reading order + prev/next derivation (PD-2). A book reads front matter →
// chapters → back matter, with volume matter interleaved at the chapter it
// anchors to; every `/read/:bookSlug/:unit` route segment resolves to one unit
// in that order. Pure functions over the catalog — unit-tested (the route param
// is an untrusted boundary: "12" is a chapter, "foreword" is a matter slug,
// "0"/"1.5"/garbage are neither).

import { BOOK_CONFIGS } from "@bindery/convex-retells/model/bookConfig";
import { divisionLabel } from "@bindery/convex-retells/model/retellingMarkdown";
import {
  findUnit,
  orderUnits,
  unitSegment as segmentOf,
} from "@bindery/convex-retells/model/unitOrder";
import type { CatalogBook } from "@bindery/shared/corpus/catalog";

/** One position in a book's reading order. */
export type ReadingUnit =
  | {
      kind: "chapter";
      number: number;
      title: string;
      premium: boolean;
      /** The product that unlocks this chapter — a volume's, once a book is
       *  priced per volume. Absent for a free chapter, or a locked chapter
       *  built from a stale cached catalog that predates this field. */
      premiumProductId?: string;
    }
  | {
      kind: "matter";
      slug: string;
      title: string;
      /** Matter is free by default and paywalled by opt-in, so this is `false`
       *  for all but the handful of documents an operator has locked. */
      premium: boolean;
      /** The product that unlocks it, when locked — a volume's, like a
       *  chapter's, resolved from the document's placement at build time. */
      premiumProductId?: string;
    };

/**
 * The full reading order: front matter, then each chapter with any matter
 * anchored immediately before or after it, then back matter.
 *
 * The interleave itself is `orderUnits` (convex/model/unitOrder.ts), shared
 * with the admin console — which derives the same order from `BOOK_CONFIGS`
 * rather than from the catalog, so the editor keeps working against a bare dev
 * deployment with nothing built. This function's own job is only to attach what
 * the CATALOG knows and config doesn't: titles, and the premium flag.
 *
 * MATTER ANCHORED TO A CHAPTER THE CATALOG DOESN'T HAVE IS DROPPED by
 * `orderUnits`, silently and on purpose — see its doc comment for why.
 * `resolveUnit` reads this same order, so such a slug 404s rather than
 * rendering an orphan page, and `shelfUnits` inherits the rule for free by
 * partitioning this list rather than re-filtering the matter array.
 */
export function readingOrder(book: CatalogBook): ReadingUnit[] {
  const chapters = new Map(book.chapters.map((c) => [c.number, c]));
  const matter = new Map(book.matter.map((m) => [m.slug, m]));

  return orderUnits(
    book.chapters.map((c) => c.number),
    book.matter,
  ).flatMap((ref): ReadingUnit[] => {
    if (ref.kind === "matter") {
      const m = matter.get(ref.slug);
      return m
        ? [
            {
              kind: "matter",
              slug: m.slug,
              title: m.title,
              premium: m.premium === true,
              premiumProductId: m.premiumProductId,
            },
          ]
        : [];
    }
    const c = chapters.get(ref.number);
    return c
      ? [
          {
            kind: "chapter",
            number: c.number,
            title: c.title,
            // Locked (paywalled) chapter — the catalog carries the flag; the
            // library and nav badge it, the gate enforces it (plan 10 §4).
            premium: c.premium === true,
            premiumProductId: c.premiumProductId,
          },
        ]
      : [];
  });
}

/** The `/read/:bookSlug/:unit` route segment for a unit. */
export function unitSegment(unit: ReadingUnit): string {
  return segmentOf(unit);
}

/** The full route path for a unit of a book. */
export function unitPath(bookSlug: string, unit: ReadingUnit): string {
  return `/read/${bookSlug}/${unitSegment(unit)}`;
}

/**
 * Resolve a raw `:unit` route param against a book's reading order. All-digits
 * params are chapter numbers; anything else is a matter slug. Returns null for
 * units the book doesn't have (the caller renders not-found).
 */
export function resolveUnit(
  book: CatalogBook,
  raw: string,
): ReadingUnit | null {
  return findUnit(readingOrder(book), raw);
}

/** The units before/after `unit` in reading order (null at either end). */
export function adjacentUnits(
  book: CatalogBook,
  unit: ReadingUnit,
): { prev: ReadingUnit | null; next: ReadingUnit | null } {
  const order = readingOrder(book);
  const i = order.findIndex((u) =>
    u.kind === "chapter" && unit.kind === "chapter"
      ? u.number === unit.number
      : u.kind === "matter" && unit.kind === "matter" && u.slug === unit.slug,
  );
  if (i === -1) return { prev: null, next: null };
  return { prev: order[i - 1] ?? null, next: order[i + 1] ?? null };
}

/**
 * Short display label for a unit ("Chapter 5", "Book α", "Letter 3" / the
 * matter title).
 *
 * Takes the book SLUG rather than a heading noun so it can name the division
 * the way the book itself does — frankenstein's sequential chapter 11 is the
 * source's "Chapter 7", which `{noun} {number}` cannot express. Falls back to
 * the bare sequential number for a slug with no config, so an unconfigured
 * book still navigates instead of throwing at the reader.
 */
export function unitLabel(unit: ReadingUnit, bookSlug: string): string {
  if (unit.kind !== "chapter") return unit.title;
  const config = BOOK_CONFIGS[bookSlug];
  return config === undefined
    ? `Chapter ${String(unit.number)}`
    : divisionLabel(config, unit.number);
}
